import React, { useState } from "react";
import { formatCurrency } from "../../../../utils/format";
import { formatDate } from "../../../../utils/date";
import { RECEIVABLE_STATUS, RECEIVABLE_STATUS_OPTIONS, PAYMENT_METHOD_LABELS, SOURCE_TYPE_LABELS } from "../constants";
import { ConfirmDialog, NeoBadge, NeoButton, NeoFinancialTable, NeoInput } from "../../../../components/Common";
import PaymentConfirmModal from "../../components/PaymentConfirmModal";
import { FINANCIAL_TYPES, FINANCIAL_STATUS } from "../../../../constants/financial";

/**
 * ReceivableTable
 * Tabela de contas a receber usando NeoFinancialTable.
 * Inclui summary inline, filtro por status, busca, expansão e ações.
 */
const ReceivableTable = ({
    records, summary, processing,
    search, statusFilter,
    onSearchChange, onStatusChange,
    onMarkAsPaid, onCancel, onLoadMore, hasMore, loading,
    hasSearched = false,
}) => {

    const [paymentRecord, setPaymentRecord] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const handleMarkPaid = (record) => {
        setPaymentRecord(record);
        setShowPaymentModal(true);
    };

    const handleConfirmPayment = async (recordId, paymentData) => {
        const success = await onMarkAsPaid(recordId, paymentData);
        if (success) {
            setShowPaymentModal(false);
            setPaymentRecord(null);
        }
    };

    const handleCancel = async (record) => {
        const confirmed = await ConfirmDialog({
            title: "Cancelar Recebível",
            text: `Deseja cancelar este recebível de ${formatCurrency(record.grossAmount)}?\nEsta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, cancelar",
            cancelButtonText: "Voltar",
        });
        if (confirmed) onCancel(record.id);
    };

    const getStatusBadge = (status) => {
        const s = RECEIVABLE_STATUS[status] || RECEIVABLE_STATUS.PENDING;
        const badgeColor = s.color === "secondary" ? "secondary" : s.color;
        return (
            <NeoBadge color={badgeColor} solid={false}>
                {s.label}
            </NeoBadge>
        );
    };

    // ── Colunas para o NeoFinancialTable ──
    const receivableColumns = [
        {
            key: 'description',
            label: 'Descrição',
            render: (row, rowIndex, { page, rowsPerPage }) => {
                const indexNum = String((page - 1) * rowsPerPage + rowIndex + 1).padStart(2, '0');
                return (
                    <div>
                        <span className="fw-bold d-block text-brand-primary font-size-12">
                            <span className="text-brand-secondary me-2">{indexNum}.</span>
                            {row.description || "—"}
                        </span>
                        {row.memberName && (
                            <small className="d-block text-muted font-size-11 neo-font-outfit">
                                {row.memberName}
                            </small>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'dueDate',
            label: 'Vencimento',
            width: '110px',
            render: (row) => (
                <span className="fw-semibold font-size-12 text-dark">
                    {formatDate(row.dueDate)}
                </span>
            )
        },
        {
            key: 'paymentDate',
            label: 'Recebimento',
            width: '110px',
            render: (row) => (
                <span className="font-size-12 text-muted">
                    {formatDate(row.paymentDate)}
                </span>
            )
        },
        {
            key: 'sourceType',
            label: 'Origem',
            width: '130px',
            render: (row) => (
                <div>
                    <span className="font-size-12 fw-semibold text-muted d-block">
                        {SOURCE_TYPE_LABELS[row.sourceType] || row.sourceType || "—"}
                    </span>
                    {row.entityName && (
                        <small className="text-muted font-size-11">{row.entityName}</small>
                    )}
                </div>
            )
        },
        {
            key: 'paymentMethod',
            label: 'Forma',
            width: '120px',
            render: (row) => (
                <div>
                    <span className="font-size-12 fw-semibold text-muted d-block">
                        {PAYMENT_METHOD_LABELS[row.paymentMethod] || row.paymentMethod || "—"}
                    </span>
                    {row.brand && (
                        <small className="text-muted font-size-11 text-capitalize">{row.brand}</small>
                    )}
                </div>
            )
        },
        {
            key: 'installment',
            label: 'Parcela',
            width: '80px',
            align: 'center',
            render: (row) => (
                <span className="font-size-12 text-muted">
                    {row.totalInstallments > 1 ? `${row.currentInstallment}/${row.totalInstallments}` : "—"}
                </span>
            )
        },
        {
            key: 'grossAmount',
            label: 'Bruto',
            width: '110px',
            align: 'right',
            render: (row) => (
                <span className="fw-bold font-size-12 text-dark">
                    {formatCurrency(row.grossAmount)}
                </span>
            )
        },
        {
            key: 'feeAmount',
            label: 'Taxa',
            width: '90px',
            align: 'right',
            render: (row) => (
                <span className="font-size-12 text-danger">
                    {row.feeAmount > 0 ? formatCurrency(row.feeAmount) : "—"}
                </span>
            )
        },
        {
            key: 'netAmount',
            label: 'Líquido',
            width: '110px',
            align: 'right',
            render: (row) => (
                <span className="fw-bold font-size-13 text-success">
                    {formatCurrency(row.netAmount)}
                </span>
            )
        },
        {
            key: 'status',
            label: 'Status',
            width: '110px',
            align: 'center',
            render: (row) => getStatusBadge(row.status)
        },
        {
            key: 'actions',
            label: 'Ações',
            width: '90px',
            align: 'center',
            render: (row) => {
                if (row.status !== FINANCIAL_STATUS.PENDING && row.status !== FINANCIAL_STATUS.OVERDUE) return null;
                return (
                    <div className="d-flex gap-1 justify-content-center">
                        <NeoButton
                            size="sm"
                            variant="outline-success"
                            square
                            disabled={processing}
                            onClick={(e) => { e.stopPropagation(); handleMarkPaid(row); }}
                            title="Dar baixa"
                            icon="mdi-check"
                        />
                        <NeoButton
                            size="sm"
                            variant="outline-danger"
                            square
                            disabled={processing}
                            onClick={(e) => { e.stopPropagation(); handleCancel(row); }}
                            title="Cancelar"
                            icon="mdi-close"
                        />
                    </div>
                );
            }
        }
    ];

    // ── Detalhes expandidos ──
    const renderRowDetails = (row) => (
        <>
            <div className="text-muted font-size-11 fw-bold text-uppercase d-flex align-items-center gap-3 flex-wrap">
                <span><i className="mdi mdi-calendar me-1"></i>Vencimento: {formatDate(row.dueDate)}</span>
                {row.paymentDate && (
                    <>
                        <span>|</span>
                        <span><i className="mdi mdi-calendar-check me-1"></i>Recebido em: {formatDate(row.paymentDate)}</span>
                    </>
                )}
                {row.paymentMethod && (
                    <>
                        <span>|</span>
                        <span><i className="mdi mdi-cash me-1"></i>{PAYMENT_METHOD_LABELS[row.paymentMethod] || row.paymentMethod}</span>
                    </>
                )}
                {row.totalInstallments > 1 && (
                    <>
                        <span>|</span>
                        <span><i className="mdi mdi-repeat me-1"></i>Parcela {row.currentInstallment}/{row.totalInstallments}</span>
                    </>
                )}
            </div>
            <div className="mt-2 d-flex justify-content-between align-items-center border-top pt-2">
                <div>
                    <span className="fw-semibold font-size-13 text-dark neo-font-outfit d-block">
                        {row.description?.toUpperCase()}
                    </span>
                    {row.memberName && (
                        <small className="text-muted font-size-11">Cliente: {row.memberName}</small>
                    )}
                </div>
                <div className="text-end">
                    {row.feeAmount > 0 && (
                        <small className="text-muted font-size-11 d-block">
                            Taxa: {formatCurrency(row.feeAmount)}
                        </small>
                    )}
                    <span className="fw-bold font-size-14 text-success neo-font-outfit">
                        {formatCurrency(row.netAmount)}
                    </span>
                </div>
            </div>
        </>
    );

    // ── Summary para o NeoFinancialTable ──
    const receivableSummary = summary ? [
        {
            label: 'Pendente',
            value: formatCurrency(summary.totalPending),
            count: summary.countPending,
            icon: 'mdi-clock-outline',
            statusClass: 'status-pending'
        },
        {
            label: 'Atrasado',
            value: formatCurrency(summary.totalOverdue),
            count: summary.countOverdue,
            icon: 'mdi-alert-circle-outline',
            statusClass: 'status-overdue'
        },
        {
            label: 'Recebido',
            value: formatCurrency(summary.totalPaid),
            icon: 'mdi-check-circle-outline',
            statusClass: 'status-paid'
        }
    ] : [];

    // ── Filtro de status (header controls) ──
    const statusFilterComponent = (
        <div className="neo-filter-select-sm">
            <NeoInput
                type="select"
                value={statusFilter}
                onChange={(e) => onStatusChange(e.target.value)}
                size="sm"
                className="mb-0"
            >
                {RECEIVABLE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </NeoInput>
        </div>
    );

    // ── Mensagens de empty state contextuais ──
    const emptyMessage = !hasSearched
        ? "Pronto para buscar"
        : "Nenhum recebível encontrado";
    const emptySubMessage = !hasSearched
        ? "Selecione o período desejado e clique em 'Buscar' para carregar os recebíveis."
        : "Ajuste os filtros ou aguarde novas vendas.";

    return (
        <>
            <NeoFinancialTable
                columns={receivableColumns}
                data={records}
                keyField="id"
                loading={loading}
                summaryData={receivableSummary}
                summaryComponent={statusFilterComponent}
                renderRowDetails={renderRowDetails}
                searchValue={search}
                onSearchChange={onSearchChange}
                searchPlaceholder="Buscar por descrição, cliente..."
                defaultItemsPerPage={20}
                itemsPerPageOptions={[10, 20, 50]}
                emptyMessage={emptyMessage}
                emptySubMessage={emptySubMessage}
            />

            {/* Load More (Firebase pagination) */}
            {hasMore && (
                <div className="text-center py-3 border-top">
                    <NeoButton
                        variant="outline-secondary"
                        size="sm"
                        onClick={onLoadMore}
                        loading={loading}
                        loadingText="Carregando..."
                        className="px-4"
                    >
                        Carregar mais
                    </NeoButton>
                </div>
            )}

            {/* Modal de baixa profissional */}
            <PaymentConfirmModal
                isOpen={showPaymentModal}
                toggle={() => { setShowPaymentModal(false); setPaymentRecord(null); }}
                record={paymentRecord}
                type={FINANCIAL_TYPES.ASSET}
                onConfirm={handleConfirmPayment}
                processing={processing}
            />
        </>
    );
};

export default ReceivableTable;
